import React from 'react';
import { Upload } from 'antd';
import Icon from '../Icon';
import Loader from '../Loader';
import { fileExtensionToMimeType } from 'utils/file';
import styled from 'styled-components';

const PrimaryText = styled.div`
  margin: 8px 0;
  font-size: 16px;
`;

export const SecondaryText = styled.div`
  color: #666;
  font-size: 14px;
`;

/**
 * FileUpload component for handling file uploads with size and type validation
 * @param {Object} props Component props
 * @param {string[]} props.fileExtensions Array of allowed file types
 * @param {number} props.maxFileSize Maximum file size in MB
 * @param {string} props.icon Icon name from common Icon component
 * @param {string} props.text Text to display next to the icon
 * @param {Function} props.onUpload Callback function to handle file upload action
 * @param {boolean} props.isUploading Whether the upload is in progress
 * @param {string} props.height Height of the upload component
 * @param {string} props.width Width of the upload component
 * @param {boolean} props.enforceFileType Whether to enforce file type
 * @param {Function} props.onError Callback function to handle upload error
 * @returns {JSX.Element} FileUpload component
 */

const FileUpload = ({
  fileExtensions,
  maxFileSize,
  icon,
  text,
  onUpload,
  isUploading,
  height = 'auto',
  width = 'auto',
  children,
  enforceFileType = true,
  onError,
}) => {
  const beforeUpload = file => {
    const isValidType = fileExtensions.some(
      extension => file.type === fileExtensionToMimeType[extension]
    );
    if (!isValidType) {
      onError?.('Invalid file type');
      return false;
    }

    const isUnderLimit = file.size / 1024 / 1024 < maxFileSize;
    if (!isUnderLimit) {
      onError?.('File too large');
      return false;
    }

    onError?.('');
    return true;
  };

  const customRequest = ({ file, onSuccess }) => {
    onUpload(file);
  };

  return (
    <Upload.Dragger
      beforeUpload={beforeUpload}
      maxCount={1}
      customRequest={customRequest}
      accept={enforceFileType ? fileExtensions.join(', ') : '*'}
      showUploadList={false}
      disabled={isUploading}
    >
      <div
        style={{
          height: height,
          width: width,
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-evenly',
        }}
      >
        {isUploading ? (
          <>
            <div>Uploading...</div>
            <Loader size='small' />
          </>
        ) : (
          <>
            <Icon icon={icon} size='5x' style={{ color: '#161616' }} />
            <PrimaryText>{text}</PrimaryText>
            <SecondaryText>{`Supported file formats: ${fileExtensions.join(
              ', '
            )}`}</SecondaryText>
            {children}
          </>
        )}
      </div>
    </Upload.Dragger>
  );
};

export default FileUpload;
